import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotesStore, useUIStore } from '../store';
import NotionEditor from './NotionEditor';
import MarkdownPreview from './MarkdownPreview';
import MarkdownToolbar from './MarkdownToolbar';
import { Save, Trash2, Tag, Clock, Eye, Edit3, Columns, Brain, Link, Share2, MoreHorizontal, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuTrigger } from './ui/context-menu';

export default function Editor({ noteId }) {
  const { currentNote, updateNote, deleteNote, backlinks, outlinks, wikiLinks, toggleStar, shareNote } = useNotesStore();
  const { editorMode, setEditorMode, addToast, toggleAiPanel } = useUIStore();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showAICommands, setShowAICommands] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => { if (currentNote) { setTitle(currentNote.title); setContent(currentNote.content); setTags(currentNote.tags || []); } }, [currentNote]);

  const handleSave = useCallback(async () => {
    if (!noteId) return;
    setIsSaving(true);
    try { await updateNote(noteId, { title, content, tags }); setLastSaved(new Date()); addToast({ title: 'Saved', type: 'success' }); }
    catch (e) { addToast({ title: 'Save failed', type: 'error' }); }
    setIsSaving(false);
  }, [noteId, title, content, tags]);

  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [handleSave]);

  const handleAddTag = (e) => { e.preventDefault(); if (tagInput && !tags.includes(tagInput)) { setTags([...tags, tagInput]); setTagInput(''); } };
  const handleRemoveTag = (t) => setTags(tags.filter(x => x !== t));
  const handleDelete = async () => { if (confirm('Delete this note?')) { await deleteNote(noteId); window.history.back(); } };
  const handleCopyContent = () => { navigator.clipboard.writeText(content); addToast({ title: 'Copied', type: 'success' }); };
  const handleDuplicateNote = async () => { const { createNote } = useNotesStore.getState(); await createNote({ title: `${title} (Copy)`, content, tags: [...tags], folder: currentNote?.folder }); addToast({ title: 'Duplicated', type: 'success' }); };

  const handleAICommand = async (cmd) => {
    if (!content) return;
    setShowAICommands(false);
    try {
      const { response } = await (await import('../api')).default.aiCommand(cmd, content);
      setAiResult(response);
      addToast({ title: `AI ${cmd} complete`, type: 'success' });
    } catch (e) { addToast({ title: 'AI command failed', type: 'error' }); }
  };

  const handleSmartTags = async () => {
    if (!title && !content) return;
    try {
      const { tags: suggested } = await (await import('../api')).default.aiSmartTags(title, content);
      const newTags = [...new Set([...tags, ...suggested])];
      setTags(newTags);
      addToast({ title: `Added ${suggested.length} tags`, type: 'success' });
    } catch (e) { addToast({ title: 'Failed to get tags', type: 'error' }); }
  };

  const handleShare = async () => {
    try {
      const { token } = await shareNote(noteId);
      navigator.clipboard.writeText(`${window.location.origin}/note/${noteId}?share=true`);
      addToast({ title: 'Share link copied', type: 'success' });
    } catch (e) { addToast({ title: 'Failed to share', type: 'error' }); }
  };

  if (!noteId) return <div className="flex items-center justify-center h-full text-muted-foreground">Select a note</div>;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-card">
            <div className="flex items-center gap-3 flex-1">
              <Input type="text" value={title} onChange={e => setTitle(e.target.value)} className="text-xl font-semibold border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-auto p-0" placeholder="Untitled" />
              {isSaving && <span className="text-xs text-muted-foreground animate-pulse">Saving...</span>}
              {lastSaved && !isSaving && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> Saved</span>}
            </div>
            <div className="flex items-center gap-1">
              <div className="flex items-center bg-muted rounded-md p-0.5">
                <Button variant={editorMode === 'edit' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setEditorMode('edit')}><Edit3 size={14} /></Button>
                <Button variant={editorMode === 'split' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setEditorMode('split')}><Columns size={14} /></Button>
                <Button variant={editorMode === 'preview' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setEditorMode('preview')}><Eye size={14} /></Button>
              </div>
              <Separator orientation="vertical" className="h-6 mx-1" />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleStar(noteId)}><Tag size={16} className={currentNote?.starred ? 'text-yellow-500' : ''} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleShare}><Share2 size={16} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleAiPanel}><Brain size={16} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleSave}><Save size={16} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={handleDelete}><Trash2 size={16} /></Button>
            </div>
          </div>

          {/* Toolbar */}
          {editorMode !== 'preview' && <MarkdownToolbar textareaRef={textareaRef} content={content} onChange={setContent} />}

          {/* Tags */}
          <div className="flex items-center gap-2 px-4 py-2 border-b bg-card">
            <Tag size={14} className="text-muted-foreground" />
            <div className="flex flex-wrap gap-1">
              {tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-secondary rounded-md group cursor-pointer hover:bg-secondary/80">
                  #{tag}
                  <button onClick={() => handleRemoveTag(tag)} className="opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                </span>
              ))}
            </div>
            <form onSubmit={handleAddTag}><Input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag..." className="w-20 text-xs h-6 focus-visible:ring-0 border-none bg-transparent" /></form>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={handleSmartTags}><Sparkles size={12} className="mr-1" /> Auto-tag</Button>
          </div>

          {/* Links & Wiki Links */}
          {(outlinks?.length > 0 || backlinks?.length > 0 || wikiLinks?.length > 0) && (
            <div className="flex items-center gap-4 px-4 py-1.5 border-b bg-muted/50 text-xs text-muted-foreground flex-wrap">
              {outlinks?.length > 0 && <span>→ {outlinks.length} link{outlinks.length > 1 ? 's' : ''}</span>}
              {backlinks?.length > 0 && <span>← {backlinks.length} backlink{backlinks.length > 1 ? 's' : ''}</span>}
              {wikiLinks?.length > 0 && <span className="flex items-center gap-1"><Link size={10} /> {wikiLinks.length} wiki link{wikiLinks.length > 1 ? 's' : ''}</span>}
            </div>
          )}

          {/* AI Commands dropdown */}
          {showAICommands && (
            <div className="absolute right-4 top-40 z-50 bg-popover border rounded-lg shadow-lg p-2 w-48">
              {['explain', 'simplify', 'expand', 'improve', 'bullet', 'formal', 'casual'].map(cmd => (
                <button key={cmd} className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-muted capitalize" onClick={() => handleAICommand(cmd)}>{cmd}</button>
              ))}
            </div>
          )}

          {/* Editor/Preview */}
          <div className="flex-1 flex overflow-hidden relative">
            {(editorMode === 'edit' || editorMode === 'split') && (
              <div className={`${editorMode === 'split' ? 'w-1/2 border-r' : 'w-full'} flex flex-col overflow-hidden`}>
                <textarea ref={textareaRef} value={content} onChange={e => setContent(e.target.value)} className="flex-1 w-full p-4 bg-transparent text-foreground font-mono text-sm leading-relaxed resize-none focus:outline-none placeholder:text-muted-foreground" placeholder="Start writing... Type '/' for commands" spellCheck={false} />
              </div>
            )}
            {(editorMode === 'preview' || editorMode === 'split') && (
              <div className={`${editorMode === 'split' ? 'w-1/2' : 'w-full'} overflow-auto`}>
                <MarkdownPreview content={aiResult || content} />
              </div>
            )}
          </div>

          {/* AI Result bar */}
          {aiResult && (
            <div className="border-t bg-muted/50 px-4 py-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">AI result shown in preview</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setContent(aiResult); setAiResult(''); }}>Apply</Button>
                <Button size="sm" variant="ghost" onClick={() => setAiResult('')}>Dismiss</Button>
              </div>
            </div>
          )}
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="w-64">
        <ContextMenuItem onClick={handleCopyContent}>Copy Content</ContextMenuItem>
        <ContextMenuItem onClick={() => navigator.clipboard.writeText(title)}>Copy Title</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleSave}>Save <ContextMenuShortcut>⌘S</ContextMenuShortcut></ContextMenuItem>
        <ContextMenuItem onClick={handleDuplicateNote}>Duplicate</ContextMenuItem>
        <ContextMenuItem onClick={handleShare}>Share</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => setEditorMode('edit')}>Edit Mode</ContextMenuItem>
        <ContextMenuItem onClick={() => setEditorMode('split')}>Split Mode</ContextMenuItem>
        <ContextMenuItem onClick={() => setEditorMode('preview')}>Preview Mode</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => setShowAICommands(s => !s)}>AI Commands</ContextMenuItem>
        <ContextMenuItem onClick={handleSmartTags}>Auto-tag</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem className="text-destructive" onClick={handleDelete}>Delete <ContextMenuShortcut>⌫</ContextMenuShortcut></ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
